// Google Analytics 4 Event Tracking Utility

/**
 * Track custom events in Google Analytics 4
 * @param {string} eventName - Name of the event
 * @param {object} params - Additional event parameters
 */
export const trackEvent = (eventName, params = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, params);
  }
};

/**
 * Track form submissions
 * @param {string} formName - Name/location of the form
 */
export const trackFormSubmission = (formName) => {
  trackEvent('form_submission', {
    form_name: formName,
    event_category: 'engagement',
    event_label: formName
  });
};

/**
 * Track CTA button clicks
 * @param {string} ctaText - Button text
 * @param {string} location - Where on page (hero, footer, etc.)
 */
export const trackCTAClick = (ctaText, location = 'page') => {
  trackEvent('cta_click', {
    cta_text: ctaText,
    cta_location: location,
    event_category: 'engagement'
  });
};

/**
 * Track product card clicks
 * @param {string} productName - Name of the product
 * @param {string} source - Where user clicked from
 */
export const trackProductClick = (productName, source = 'product_page') => {
  trackEvent('product_click', {
    product_name: productName,
    click_source: source,
    event_category: 'product_interest'
  });
};

/**
 * Track megamenu interactions
 * @param {string} menuSection - Which megamenu section
 * @param {string} itemClicked - What was clicked
 */
export const trackMegamenuClick = (menuSection, itemClicked) => {
  trackEvent('megamenu_click', {
    menu_section: menuSection,
    item_clicked: itemClicked,
    event_category: 'navigation'
  });
};

/**
 * Track downloads
 * @param {string} downloadName - What was downloaded
 */
export const trackDownload = (downloadName) => {
  trackEvent('file_download', {
    file_name: downloadName,
    event_category: 'engagement'
  });
};

/**
 * Track landing page visits
 * @param {string} landingPageName - Name of the landing page
 */
export const trackLandingPageView = (landingPageName) => {
  trackEvent('landing_page_view', {
    page_name: landingPageName,
    event_category: 'landing_pages'
  });
};

/**
 * Track video plays
 * @param {string} videoTitle - Title of the video
 */
export const trackVideoPlay = (videoTitle) => {
  trackEvent('video_play', {
    video_title: videoTitle,
    event_category: 'engagement'
  });
};

/**
 * Track outbound link clicks
 * @param {string} url - External URL clicked
 */
export const trackOutboundLink = (url) => {
  trackEvent('outbound_click', {
    link_url: url,
    event_category: 'outbound'
  });
};

/**
 * Track blog article engagement
 * @param {string} articleTitle - Title of the article
 * @param {string} action - read, share, etc.
 */
export const trackBlogEngagement = (articleTitle, action = 'read') => {
  trackEvent('blog_engagement', {
    article_title: articleTitle,
    engagement_type: action,
    event_category: 'content'
  });
};

/**
 * Track scroll depth
 * @param {number} percentage - How far user scrolled (25, 50, 75, 100)
 */
export const trackScrollDepth = (percentage) => {
  trackEvent('scroll_depth', {
    scroll_percentage: percentage,
    event_category: 'engagement'
  });
};
